/**
 * TopPrioritiesCard - Phase 3.1 Dashboard Enhancement
 * 
 * Shows the top 3 highest-impact pending items that need attention.
 * Each item shows expected claim impact and provides one-click navigation.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  AlertTriangle,
  DollarSign,
  FileText,
  ArrowRight,
  Clock,
  Loader2,
  Target,
  TrendingUp,
  CheckCircle
} from 'lucide-react';

interface PriorityItem {
  id: string;
  title: string;
  description: string;
  type: 'missing_data' | 'needs_review' | 'classification' | 'calculation';
  impact: number; // Dollar impact on claim
  urgency: 'critical' | 'high' | 'medium';
  route: string;
  draftId?: string;
  reportId?: string;
}

interface TopPrioritiesCardProps {
  maxItems?: number;
  className?: string;
  reportId?: string; // If provided, show priorities for this specific case
}

const URGENCY_STYLES = {
  critical: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    badge: 'bg-red-100 text-red-700'
  },
  high: {
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'text-orange-600',
    badge: 'bg-orange-100 text-orange-700'
  },
  medium: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    badge: 'bg-yellow-100 text-yellow-700'
  }
};

const TYPE_ICONS = {
  missing_data: FileText,
  needs_review: AlertTriangle,
  classification: Target,
  calculation: TrendingUp
};

export default function TopPrioritiesCard({ maxItems = 3, className = '', reportId }: TopPrioritiesCardProps) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [priorities, setPriorities] = useState<PriorityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPriorities();
  }, [reportId]);

  const fetchPriorities = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      
      // If reportId provided, fetch case-specific priorities
      const endpoint = reportId 
        ? `/api/v1/queue/case/${reportId}/priorities`
        : '/api/v1/dashboard/top-priorities';
      
      const data = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setPriorities((data.priorities || []).slice(0, maxItems));
    } catch (err) {
      console.debug('Top priorities API not available');
      setPriorities([]);
      setError('unavailable');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleItemClick = (item: PriorityItem) => {
    navigate(item.route);
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Top Priorities</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  if (priorities.length === 0) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Top Priorities</h3>
        </div>
        {error ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <AlertTriangle className="w-8 h-8 text-slate-400 mb-2" />
            <p className="text-sm text-slate-500">Priorities unavailable</p>
            <button
              onClick={fetchPriorities}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
            <p className="text-gray-600 font-medium">All caught up!</p>
            <p className="text-gray-500 text-sm">No high-priority items need attention</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Top Priorities</h3>
        <span className="text-xs text-gray-500">
          {priorities.length} item{priorities.length !== 1 ? 's' : ''} need attention
        </span>
      </div>

      <div className="space-y-3">
        {priorities.map((item, index) => {
          const styles = URGENCY_STYLES[item.urgency];
          const Icon = TYPE_ICONS[item.type];

          return (
            <button
              key={item.id}
              onClick={() => handleItemClick(item)}
              className={`w-full text-left p-4 rounded-lg border ${styles.bg} ${styles.border} hover:shadow-md transition-all group`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg bg-white ${styles.icon}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 truncate">
                        {item.title}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles.badge}`}>
                        {item.urgency}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {item.description}
                    </p>
                    {item.impact > 0 && (
                      <div className="flex items-center mt-2 text-sm">
                        <DollarSign className="w-4 h-4 text-green-600 mr-1" />
                        <span className="text-green-700 font-medium">
                          {formatCurrency(item.impact)} potential impact
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
